/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { MrfiktivHttpClientProvider } from "../mrfiktiv-http-client.provider";

import {
  MrfiktivCreateRentalDtoGen,
  MrfiktivPageViewModelGen,
  MrfiktivRentalControllerGetParamsGen,
  MrfiktivRentalViewModelGen,
  MrfiktivUpdateRentalDtoGen
} from "../v1/data-contracts";
import { Rental } from "../v1/Rental";

class RentalService {
  mrfiktivProxy: Rental;

  /**
   * Construct service
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider) {
    this.mrfiktivProxy = new Rental(mrfiktivHttpClientProvider.client());
  }

  /**
   * Creates a new rental
   * @param partnerId The partner ID
   * @param data The rental data
   * @returns The created rental
   */
  async create(partnerId: string, data: MrfiktivCreateRentalDtoGen): Promise<MrfiktivRentalViewModelGen> {
    return (await this.mrfiktivProxy.rentalControllerCreate(partnerId, data)).data;
  }

  /**
   * Gets all rentals for a partner
   * @param partnerId The partner ID
   * @param params Optional request parameters
   * @returns A list of rentals
   */
  async getAll(data: MrfiktivRentalControllerGetParamsGen): Promise<
    MrfiktivPageViewModelGen & {
      data?: MrfiktivRentalViewModelGen[];
    }
  > {
    const rentals = (await this.mrfiktivProxy.rentalControllerGet(data)).data;

    return rentals;
  }

  /**
   * Gets a specific rental by ID
   * @param partnerId The partner ID
   * @param rentalId The rental ID
   * @returns The rental
   */
  async getOne(partnerId: string, rentalId: string): Promise<MrfiktivRentalViewModelGen> {
    return (await this.mrfiktivProxy.rentalControllerGetOne(partnerId, rentalId)).data;
  }

  /**
   * Updates a rental
   * @param partnerId The partner ID
   * @param rentalId The rental ID
   * @param data The rental update data
   * @returns The updated rental
   */
  async update(
    partnerId: string,
    rentalId: string,
    data: MrfiktivUpdateRentalDtoGen
  ): Promise<MrfiktivRentalViewModelGen> {
    return (await this.mrfiktivProxy.rentalControllerUpdate(partnerId, rentalId, data)).data;
  }

  /**
   * Updates the status of a rental
   * @param partnerId The partner ID
   * @param rentalId The rental ID
   * @returns The updated rental
   */
  async updateStatus(partnerId: string, rentalId: string): Promise<MrfiktivRentalViewModelGen> {
    return (await this.mrfiktivProxy.rentalControllerUpdateStatus(partnerId, rentalId)).data;
  }

  /**
   * Deletes a rental
   * @param partnerId The partner ID
   * @param rentalId The rental ID
   * @returns The deleted rental
   */
  async delete(partnerId: string, rentalId: string): Promise<MrfiktivRentalViewModelGen> {
    return (await this.mrfiktivProxy.rentalControllerDelete(partnerId, rentalId)).data;
  }

  /**
   * Gets a rental by shared access token
   * @param partnerId The partner ID
   * @param rentalId The rental ID
   * @param token The shared access token
   * @returns The rental
   */
  async getDocumentBySharedAccessToken(
    partnerId: string,
    rentalId: string,
    token: string
  ): Promise<{ rental: MrfiktivRentalViewModelGen }> {
    const shared = await this.mrfiktivProxy.rentalControllerAccess({
      partnerId,
      rentalId,
      sct: token
    });

    return { rental: shared.data };
  }
}

/**
 * Communicate with Rental API
 */
export default new RentalService(new MrfiktivHttpClientProvider());
